import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/services/api.service';
import { SystemPropertiesService } from '@app/core/services/system-properties.service';
import { ValuationState } from '@common/enums/valuation/ValuationState';
import { IApiStatus } from '@common/models/IApiStatus';
import { IAttachment } from '@common/models/IAttachment';
import { IPortfolioCompany } from '@common/models/IPortfolioCompany';
import { IAttachmentType } from '@common/models/ISystemProperty';
import { IValuation } from '@common/models/IValuation';
import { Observable } from 'rxjs';
import { getLastDateInQuarter, getQuarter } from '@common/services/DateHelper';
import { CapitalMemberType } from '@common/enums/CapitalMemberType';
import CompanyRecalcService from "@common/services/CompanyRecalcService";
import { InputSource } from '@common/enums/InputSource';
import { IFundCapitalMember } from '@common/models/ICapitalMember';
import { IConfig, IValuationWriteupQCSConsolidatedConfig } from '@common/models/IConfig';
import { SystemPropertyKey } from '@common/enums/SystemPropertyKey';
import { AttachmentType } from '@common/enums/capitalCall/AttachmentType';
@Injectable({
  providedIn: 'root'
})
export class ValuationService {
  public recalcService: any;
  
  constructor(private readonly systemPropertiesService: SystemPropertiesService, private readonly apiService: ApiService) {

   }
  public async setAttachments(valuation: IValuation, company, fundCapitalMembers?:IFundCapitalMember[], configData?: IConfig):Promise<void> {
    // The system properties key for the attachment types of the given valuation type
    const configValue = configData?.config_value as IValuationWriteupQCSConsolidatedConfig;
    const key: string = SystemPropertyKey.valuationAttachmentTypes;
    valuation.attachments = [];
    const asOfDate = getLastDateInQuarter(valuation.name, false);
    this.recalcService = CompanyRecalcService.newTrimmedCalculator(company, asOfDate);
    const fundsArray: any[] = [];
    let excludeInAttachment  = false;
    for (const member of company.capitalMembers || []) {
      excludeInAttachment = fundCapitalMembers?.find(fundCapMem=>fundCapMem.name == member.name)?.excludeInTearsheet
        if ((member.memberType == CapitalMemberType.MainFund || member.memberType == CapitalMemberType.Coinvestment) && !excludeInAttachment) {
            fundsArray.push({value: member.name});
      }
    }
    
    return new Promise<void>((resolve, reject) => {
      this.systemPropertiesService.getByKey<IAttachmentType[]>(key).subscribe({ 
        next: ((attachmentTypes) => {
          let count = 0
          const types = attachmentTypes;
          for (let i = 0; i < types.length; i++) {
            let type = types[i];
            if(type.name === AttachmentType.valuationWriteUp){
                for(let j=0; j<fundsArray.length; j++){
                    valuation.attachments.push({
                      fileName: '',
                      attachmentType: type.name ,
                      index: count ,
                      mimeType: '',
                      path: '',
                      typeDefinition: type
                    });
                    count++;
                }
            }
            else{
              const typeNameCheck = (type.name == configValue?.name);
              const allInB = company.fundFamily.some(x =>  configValue?.fundFamily?.includes(x));
              if (typeNameCheck) {
                if (allInB) {
                  valuation.attachments.push({
                    fileName: '',
                    attachmentType: type.name,
                    index: 0,
                    mimeType: '',
                    path: '',
                    typeDefinition: type
                  });
                }
              } else {
                valuation.attachments.push({
                  fileName: '',
                  attachmentType: type.name,
                  index: 0,
                  mimeType: '',
                  path: '',
                  typeDefinition: type
                });
              }

              count ++;
            }
          }
          resolve();
          
        }),        
        error: ((err)  => reject(err))
      })
    })
  }
  public async setAttachmentTypes(valuations: IValuation, company, fundCapitalMembers?:IFundCapitalMember[], configData?: IConfig):Promise<void> {
    const configValue = configData?.config_value as IValuationWriteupQCSConsolidatedConfig;
    const asOfDate = getLastDateInQuarter(valuations.name, false);
    this.recalcService = CompanyRecalcService.newTrimmedCalculator(company, asOfDate);
    const fundsArray: any[] = [];
    let excludeInAttachment  = false;
    for (const member of this.recalcService.company.capitalMembers) {
      excludeInAttachment = fundCapitalMembers?.find(fundCapMem=>fundCapMem.name == member.name)?.excludeInTearsheet
      if ((member.memberType == CapitalMemberType.MainFund || member.memberType == CapitalMemberType.Coinvestment ) && !excludeInAttachment ) {
        fundsArray.push({ value: member.name }); 
      }
    }
    const key: string = SystemPropertyKey.valuationAttachmentTypes;
    return new Promise<void>((resolve, reject) => {
      this.systemPropertiesService.getByKey<IAttachmentType[]>(key).subscribe({
        next: ((attachmentTypes) => {
          const values = attachmentTypes;
          let writeUpAttachments: any[] = [];  // To store all ValuationWriteUp attachments
          let nonWriteUpAttachments: any[] = [];  // To store all other attachment types
          this.processAttachment(values, valuations, fundsArray, writeUpAttachments, nonWriteUpAttachments)
          const processedWriteUps = new Set(valuations.attachments.filter(a => a.attachmentType === AttachmentType.valuationWriteUp).map((a, idx) => idx));
          let valuationWriteUpIndex = 0;

          // If there are extra funds that haven't been added, create new attachment rows for them
          while (valuationWriteUpIndex < fundsArray.length) {
            // Skip funds that already have a ValuationWriteUp attachment
            if(!processedWriteUps.has(valuationWriteUpIndex)) {
                const newAttachment: any = {
                  fileName: '',
                  attachmentType: AttachmentType.valuationWriteUp,
                  index: valuationWriteUpIndex,
                  mimeType: '',
                  path: '',
                  typeDefinition: {
                    ...values.find((t: IAttachmentType) => t.name === AttachmentType.valuationWriteUp),
                  },
                };
      
              let desc = newAttachment?.typeDefinition?.description + ' ' + `(${fundsArray[valuationWriteUpIndex]?.value})`;
              newAttachment.typeDefinition.description = desc;
      
              writeUpAttachments.push(newAttachment); 
            }
            valuationWriteUpIndex++; // Increment to handle the next fund
          }
           // Now handle for extra attachment types in values that aren't already in valuations.attachments
          for (const value of values) {
            const existingAttachment = valuations.attachments.find(a => a.attachmentType === value.name);
            if (!existingAttachment) {
              const typeNameCheck = (value.name == configValue?.name);
              const allInB = company.fundFamily.some(x => configValue?.fundFamily?.includes(x));
              if (typeNameCheck) {
                if (allInB) {
                  const newAttachment: any = {
                    fileName: '',
                    attachmentType: value.name,
                    index: 0, 
                    mimeType: '',
                    path: '',
                    typeDefinition: { ...value },
                  };
                  // Add the new attachment to the nonWriteUpAttachments
                  nonWriteUpAttachments.push(newAttachment);
                }
              } else {
                const newAttachment: any = {
                  fileName: '',
                  attachmentType: value.name,
                  index: 0, 
                  mimeType: '',
                  path: '',
                  typeDefinition: { ...value },
                };
  
                // Add the new attachment to the nonWriteUpAttachments
                nonWriteUpAttachments.push(newAttachment);
              }

            }
          }
          valuations.attachments = [...writeUpAttachments, ...nonWriteUpAttachments];
          resolve();
        }),
        error: ((err)  => reject(err))
      });
    });   
  }
  processAttachment(values, valuations, fundsArray, writeUpAttachments, nonWriteUpAttachments){
    let fundsIndex = 0; 
    for (let attachment of valuations.attachments) {
      const type = values.find((t: IAttachmentType) => t.name === attachment.attachmentType);
      const t = { ...type };
      
      if (t.name === AttachmentType.valuationWriteUp) {
          if (fundsArray[fundsIndex]) {
            attachment.typeDefinition = t;
            let desc = `${attachment?.typeDefinition?.description} (${fundsArray[fundsIndex]['value']})`;
            attachment.typeDefinition.description = desc;
            attachment.fundName = fundsArray[fundsIndex]['value'];
            fundsIndex++;
            writeUpAttachments.push(attachment);
          }
      } else {
          attachment.typeDefinition = attachment.typeDefinition ? attachment.typeDefinition : t;
          nonWriteUpAttachments.push(attachment);
          
      }
    }
  }

  generateNextQuarters(date){
    const today = new Date(date);
    const quarter = Math.floor(today.getMonth() / 3); let nextQuarterArr = []
      for(let i=1; i<=12; i++){
        let startDate = new Date(today.getFullYear(), quarter * 3 + (i*3), 1);
        let endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);

        let date : any = new Date(endDate);
        date = date.getFullYear() + "/"+(date.getMonth()+1) +"/"+date.getDate() ;

        nextQuarterArr.push({'name' : date })
       
      }
    return nextQuarterArr
  }
  generateNextQuarter(date1){
    const today = new Date(date1);
    const quarter = Math.floor(today.getMonth() / 3); let nextQuarterArr = []
        let startDate = new Date(today.getFullYear(), quarter * 3 + (3), 1);
        let endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);

        let date : any = new Date(endDate);
        date = date.getFullYear() + "/"+(date.getMonth()+1) +"/"+date.getDate() ;

        nextQuarterArr.push({'name' : getQuarter(endDate)})
       
      // }
    return nextQuarterArr
  }
  public newValuation(existing, company){
    let name = ''
    if(existing.length >0){
      let sortedValutions = existing.sort((x, y) => {
        return new Date(y.effectiveDate).getTime() - new Date(x.effectiveDate).getTime();
      })
      let arr = this.generateNextQuarter(sortedValutions[0]['effectiveDate'])
      name = arr[0]['name'];
    }
    else{
      if(company.investedCapital.length >0){
        let sortedCC = company.investedCapital.sort((x, y) => {
          return new Date(x.fundingDate).getTime() - new Date(y.fundingDate).getTime();
        })
        let arr = getQuarter(new Date(sortedCC[0]['fundingDate']))
        name = arr;
      }
    }

    return {
      name,
      state: ValuationState.Requested,
      ntmEbitda:0,
      enterpriseValue: 0,
      holdingValue: 0,
      id: existing.id -1,
      effectiveDate: getLastDateInQuarter(name, false),
      requestedDate: new Date(Date.now()),
      valuationAmount:0,
      attachments: [],
      currency: "USD",
      source: InputSource.QUILT,
      comments: ''
    }
  }

  public get(mnemonic: string): Observable<IApiStatus> {
    return this.apiService.get(`PortfolioCompany/${mnemonic}/Valuation`)
  }

  public add(mnemonic: string, requestBody: IValuation): Observable<IApiStatus> {
    return this.apiService.post(`PortfolioCompany/${mnemonic}/Valuation`, requestBody)
  }
  public delete(mnemonic: string, name: string): Observable<IApiStatus> {
    return this.apiService.delete(`PortfolioCompany/${mnemonic}/Valuation/${name}`)
  }
  public update(mnemonic: string, requestBody: IValuation): Observable<IApiStatus> {
    return this.apiService.put(`PortfolioCompany/${mnemonic}/Valuation`, requestBody)
  }
  public preview(portfolioCompany: IPortfolioCompany, name): Observable<any> {
    const url = `PortfolioCompany/${portfolioCompany.mnemonic}/Valuation/${name}/Preview`;
    return this.apiService.post(url, portfolioCompany, {
      headers: new HttpHeaders().set('Accept', "application/pdf"),
      responseType: 'blob' as 'json'
    });
  }
  uploadAttachment(portfolioCompany: IPortfolioCompany, valuation: IValuation, attachment: IAttachment, formData: FormData): Observable<IAttachment>{
    let desc = attachment.description ? attachment.description : attachment.typeDefinition.description;
    return this.apiService.post(`PortfolioCompany/${portfolioCompany.mnemonic}/Valuation/${valuation.name}/Attachment/${attachment.attachmentType}/${attachment.index }/?description=${desc}`, formData)
  }
  public deleteAttachment(mnemonic: string, name: string, attachment: IAttachment): Observable<IApiStatus> {
    return  this.apiService.delete(`PortfolioCompany/${mnemonic}/Valuation/${name}/Attachment/${attachment.attachmentType}/${attachment.index || 0}/${attachment.fileName}`)
  }
  public fileUploadPreview(portfolioCompany: IPortfolioCompany, valuation: IValuation, attachment: IAttachment): Observable<Blob> {
    const url = `PortfolioCompany/${portfolioCompany.mnemonic}/Valuation/${valuation.name}/Attachment/Preview`;
    return this.apiService.post(url, attachment, {
      headers: new HttpHeaders().set('Accept', attachment.mimeType),
      responseType: 'blob'
    }) 
  }

  public getValList(company: IPortfolioCompany){
    return new Promise<any>((resolve, reject) => {
      this.get(company.mnemonic).subscribe((res) =>{
        let valList:any = [], valuationArr: any[] = []
        valList = res
        if(valList.length >0){
          for(let i =0 ; i<valList.length;i++){
            let row = valList[i]
            let valObj: any ={}
            //initialize an id as a unique field 
            valObj['id'] = i 
            valObj['state'] = row['state']
            valObj['name'] = (row['name']);
            valObj['quarterEndDate'] = getLastDateInQuarter(row['name'], false);
            valObj['investedCapital'] = row['investedCapital'];
            valObj['realizedValue'] = row['realizedValue'];
            valObj['grossMoic'] = row['grossTotalMoic'];
            valObj['holdingValue'] = row['holdingValue'];
            valObj['totalValue'] = row['totalValue'];
            valObj['irr'] =  row['xirr']
            valObj['enterpriseValue'] = row?.['enterpriseValue'] ? row['enterpriseValue'] : 0
            valObj['ntmEbitda'] = row?.['ntmEbitda'] ? row['ntmEbitda'] : 0
            valObj['capitalMembers']= row?.['capitalMembers']
            valObj['methodology'] = row?.['methodology']
            
            valuationArr.push(valObj)
          }
          resolve(valuationArr);
          
        }
        else{
          resolve(valuationArr);
        }
      })
    });
  }
  public getFundSplitCapitalMemberTable(company, valuation, asOfDate, shadowMemberHoldingValues?: {[memberName: string]: number}, shadowMemberIrr?: {[memberName: string]: number}){
    this.recalcService = CompanyRecalcService.newTrimmedCalculator(company, asOfDate);
    let fundCapMemberArray:any[] = [] ;
    this.systemPropertiesService.getByKey<any[]>(SystemPropertyKey.fundSplitIRR).subscribe({
        next: ((fundSplitPCs) => {
        const isFundSplitPC = fundSplitPCs ? fundSplitPCs.includes(company.mnemonic) : false;
    const eligibleMembers = this.recalcService.company.capitalMembers.filter(
      m => m.memberType == CapitalMemberType.MainFund || m.memberType == CapitalMemberType.Coinvestment || m.memberType == CapitalMemberType.DirectCoinvestment
    );
    const totalCommitment = eligibleMembers.reduce((sum, m) => sum + (m.commitment || 0), 0);
    const nonShadowCommitment = shadowMemberHoldingValues
      ? eligibleMembers.filter(m => shadowMemberHoldingValues[m.name] == null).reduce((sum, m) => sum + (m.commitment || 0), 0)
      : 0;
    for (const member of this.recalcService.company.capitalMembers) {
      if (member.memberType == CapitalMemberType.MainFund || member.memberType == CapitalMemberType.Coinvestment || member.memberType == CapitalMemberType.DirectCoinvestment) {
        let ratio = this.recalcService.fundRatio(member.name, asOfDate);
        if (!Number.isFinite(ratio)) {
          ratio = totalCommitment > 0 ? (member.commitment || 0) / totalCommitment : 0;
        }
        let hvRatio = ratio;
        if (shadowMemberHoldingValues && shadowMemberHoldingValues[member.name] == null) {
          hvRatio = nonShadowCommitment > 0 ? (member.commitment || 0) / nonShadowCommitment : 0;
        }
        const contributions = company?.splitLogicEnabled ? this.recalcService.fundWiseContributionByDate(asOfDate, member.name) : this.allocatedMetric(this.recalcService.totalContributions(), ratio);
        const hv = shadowMemberHoldingValues?.[member.name] != null
          ? shadowMemberHoldingValues[member.name]
          : this.allocatedMetric(valuation.holdingValue, hvRatio);
        const realizedValue = company?.splitLogicEnabled ? this.recalcService.fundWiseDistributionByDate(asOfDate, member.name) : this.allocatedMetric(valuation.realizedValue, ratio);
        const tv = hv + realizedValue;
        const moic = contributions > 0 ? tv / contributions : 0;
        let fundSummary : any = {
          fund: { displayName: "Fund", value: member.name},
          contributions: {displayName: "Contributions", value: contributions},
          holdingValue: {displayName: "Holding Value" , value: hv},
          realizedValue: {displayName: "Realized Value", value: realizedValue},
          grossMoic: {displayName: "MOIC", value: moic},
          irr: {displayName: "IRR", value: shadowMemberIrr?.[member.name] != null
            ? shadowMemberIrr[member.name]
            : (isFundSplitPC || company?.splitLogicEnabled ? this.recalcService.xirr(asOfDate, member.name, isFundSplitPC) : this.recalcService.xirr(asOfDate))},
          totalValue: {displayName: "Total Value", value: tv},
        }
        fundCapMemberArray.push(fundSummary);
      }
    }
        }),
        error: ((err)  => (err))
      })

    return fundCapMemberArray
  }
  public buildShadowMemberHoldingValues(company, shadowMembers: string[], shadowHoldingValue: number, asOfDate: Date): {[name: string]: number} {
    const recalc = CompanyRecalcService.newTrimmedCalculator(company, asOfDate);
    const map: {[name: string]: number} = {};
    let totalRatio = 0;
    const ratios: {[name: string]: number} = {};
    let useCommitmentRatio = false;
    for (const memberName of shadowMembers) {
      const ratio = recalc.fundRatio(memberName, asOfDate);
      if (!Number.isFinite(ratio)) {
        useCommitmentRatio = true;
        break;
      }
      ratios[memberName] = ratio;
      totalRatio += ratio;
    }
    if (useCommitmentRatio) {
      totalRatio = 0;
      const allMembers = company.capitalMembers || [];
      for (const memberName of shadowMembers) {
        const member = allMembers.find(m => m.name === memberName);
        const commitment = member?.commitment || 0;
        ratios[memberName] = commitment;
        totalRatio += commitment;
      }
    }
    for (const memberName of shadowMembers) {
      const normalizedRatio = totalRatio > 0 ? ratios[memberName] / totalRatio : 0;
      map[memberName] = shadowHoldingValue * normalizedRatio;
    }
    return map;
  }
  allocatedMetric(total: any, ratio: number): any {
    return <number>total * ratio;
  }
  calcGrossMoic(valuation, ratio){
    let investedCapital = this.allocatedMetric(this.recalcService.totalContributions(), ratio);
    const grossTotalMoic = (this.allocatedMetric(valuation.realizedValue, ratio) + this.allocatedMetric(valuation.holdingValue, ratio)) / investedCapital;
    return Number.isFinite(grossTotalMoic) ? grossTotalMoic : 0;
  }
  public getVersionAttachmentList(mnemonic, name, fileName) : any{
      return this.apiService.get(`PortfolioCompany/${mnemonic}/Valuation/${name}/Attachment/${fileName}`)
  }

  public getAggregateHoldingValue(mnemonic, quarter) : any{
      return this.apiService.get(`PortfolioCompany/${mnemonic}/Valuation/aggregate/${quarter}`)
  }

  public downloadAttachmentVersion(portfolioCompany, name,  attachment, version): Observable<Blob> {
    const url = `PortfolioCompany/${portfolioCompany.mnemonic}/Valuation/${name}/Attachment/${version}`;
    return this.apiService.post(url, attachment, {
      headers: new HttpHeaders().set('Accept', attachment.mimeType),
      responseType: 'blob'
    }) 

  }
  public approvedValuation(mnemonic: string,name:string, requestBody: IPortfolioCompany): Observable<IApiStatus> {
    return this.apiService.post(`PortfolioCompany/${mnemonic}/Valuation/${name}/Approval/Approve`, requestBody)
  }
  public getRecipients(company:IPortfolioCompany){
    let signatoriesList: any[] = [];
    this.systemPropertiesService.getByKey<any>(SystemPropertyKey.valuationSignatoriesExcludeList).subscribe((signatories) => {
      signatoriesList.push(signatories);
      
    });
    signatoriesList = signatoriesList[0].map((s: string)=>s.toLocaleLowerCase())
    const recipient:any[] = [];
    company.team.boardMembers.forEach(member=>{
        if(signatoriesList.indexOf(member.toLocaleLowerCase()) == -1 && recipient.length == 0){
            recipient.push(member.toLocaleLowerCase());
        }
    })
    return recipient
  }
  public versionHistory(mnemonic: string, model, submodel, name): Observable<any> {
    return this.apiService.get(`VersionHistory/${mnemonic}/${model}/${submodel}/${name}?objectID=`)
  }

}
